import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area, ComposedChart } from 'recharts';
import Http from '../../Common/Http';
// import ReactDataTable from '../../Common/ReactDataTable.jsx';

const SupplyChain = () => {
  const navigate = useNavigate();
  const [rawMaterialsData, setRawMaterialsData] = useState([]);
  const [packingMaterialsData, setPackingMaterialsData] = useState([]);
  const [stockData, setStockData] = useState([]);
  const [filteredStockData, setFilteredStockData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTimeframe, setSelectedTimeframe] = useState("monthly");
  const [searchTerm, setSearchTerm] = useState("");

  // Handle product click to navigate to product ledger
  const handleProductClick = (product) => {
    navigate(`/product-ledger?productId=${product.id}&productCode=${product.code}&productName=${encodeURIComponent(product.name)}`);
  };
  // Fetch data from backend
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch stock data
        const stockResponse = await Http.get('/product-wise-stock');
        if (stockResponse.data.status === 'success') {
          setStockData(stockResponse.data.item);
          setFilteredStockData(stockResponse.data.item);
        } else {
          setStockData([]);
          setFilteredStockData([]);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching supply chain data:', error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Search filtering effect
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredStockData(stockData);
    } else {
      const filtered = stockData.filter(product => 
        (product.name && product.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (product.code && product.code.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (product.category_name && product.category_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (product.unit_name && product.unit_name.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredStockData(filtered);
    }
  }, [searchTerm, stockData]);

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle search form submit
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    // Search is handled by the useEffect above
  };

  // Chart data processing functions
  const getStockStatusData = () => {
    if (!stockData.length) return [];
    
    const inStock = stockData.filter(product => parseFloat(product.stock_qty || 0) > 10).length;
    const lowStock = stockData.filter(product => {
      const qty = parseFloat(product.stock_qty || 0);
      return qty <= 10 && qty > 5;
    }).length;
    const critical = stockData.filter(product => parseFloat(product.stock_qty || 0) <= 5).length;
    
    return [
      { name: 'In Stock', value: inStock, color: '#28a745' },
      { name: 'Low Stock', value: lowStock, color: '#ffc107' },
      { name: 'Critical', value: critical, color: '#dc3545' }
    ];
  };

  const getCategoryWiseData = () => {
    if (!stockData.length) return [];
    
    const categoryMap = {};
    stockData.forEach(product => {
      const category = product.category_name || 'Uncategorized';
      const qty = parseFloat(product.stock_qty || 0);
      
      if (!categoryMap[category]) {
        categoryMap[category] = { category, totalStock: 0, productCount: 0 };
      }
      categoryMap[category].totalStock += qty;
      categoryMap[category].productCount += 1;
    });
    
    return Object.values(categoryMap).sort((a, b) => b.totalStock - a.totalStock);
  };

  const getTopProductsData = () => {
    if (!stockData.length) return [];
    
    return stockData
      .map(product => ({
        name: product.name || 'N/A',
        code: product.code || 'N/A',
        stock: parseFloat(product.stock_qty || 0),
        unit: product.unit_name || 'pcs'
      }))
      .sort((a, b) => b.stock - a.stock)
      .slice(0, 10);
  };

  const getStockTrendData = () => {
    if (!stockData.length) return [];
    
    // Simulate trend data based on current stock levels
    const categories = [...new Set(stockData.map(p => p.category_name || 'Uncategorized'))];
    
    return categories.slice(0, 6).map(category => {
      const categoryProducts = stockData.filter(p => (p.category_name || 'Uncategorized') === category);
      const totalStock = categoryProducts.reduce((sum, p) => sum + parseFloat(p.stock_qty || 0), 0);
      const avgStock = totalStock / categoryProducts.length;
      
      return {
        category,
        current: Math.round(avgStock),
        projected: Math.round(avgStock * (0.8 + Math.random() * 0.4)), // Simulate variation
        target: Math.round(avgStock * 1.2)
      };
    });
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <style>{`
        .hover-row:hover {
          background-color: #f8f9fa !important;
          transform: scale(1.01);
          transition: all 0.2s ease;
        }
        .hover-row {
          transition: all 0.2s ease;
        }
      `}</style>
      <div className="am-mainpanel">
        <div className="am-pagetitle">
          <h5 className="am-title">Supply Chain Dashboard</h5>
          <form id="searchBar" className="search-bar" onSubmit={handleSearchSubmit}>
            <div className="form-control-wrapper">
              <input
                type="search"
                className="form-control bd-0"
                placeholder="Search products by name, code, category, or unit..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>
            <button id="searchBtn" className="btn btn-orange" type="submit">
              <i className="fa fa-search" />
            </button>
            {searchTerm && (
              <button 
                type="button" 
                className="btn btn-secondary ml-2"
                onClick={() => setSearchTerm('')}
                title="Clear search"
              >
                <i className="fa fa-times" />
              </button>
            )}
          </form>
        </div>

        <div className="am-pagebody">
          <div className="row row-sm mg-t-20">
            <div className='col-md-12'>
              <div className='card pd-20 pd-sm-40'>
                {/* Stock Summary Cards */}
                {stockData.length > 0 && (
                  <div className="row mt-3">
                    <div className="col-md-3">
                      <div className="card bg-primary text-white">
                        <div className="card-body text-center">
                          <h4>{stockData.length}</h4>
                          <h6>Total Products</h6>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="card bg-success text-white">
                        <div className="card-body text-center">
                          <h4>
                            {stockData.filter(product => {
                              const currentStock = parseFloat(product.stock_qty || 0);
                              return currentStock > 10; // In stock threshold
                            }).length}
                          </h4>
                          <h6>In Stock</h6>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="card bg-warning text-white">
                        <div className="card-body text-center">
                          <h4>
                            {stockData.filter(product => {
                              const currentStock = parseFloat(product.stock_qty || 0);
                              return currentStock <= 10 && currentStock > 5; // Low stock range
                            }).length}
                          </h4>
                          <h6>Low Stock</h6>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="card bg-danger text-white">
                        <div className="card-body text-center">
                          <h4>
                            {stockData.filter(product => {
                              const currentStock = parseFloat(product.stock_qty || 0);
                              return currentStock <= 5; // Critical threshold
                            }).length}
                          </h4>
                          <h6>Critical</h6>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* Product-wise Stock Table */}
          <div className="row row-sm mg-t-20">
            <div className="col-md-12">
              <div className="card pd-20 pd-sm-40">
                {/* Charts Section */}
                {stockData.length > 0 && (
                  <div className="row mt-4">
                    {/* Stock Status Distribution Pie Chart */}
                    <div className="col-md-6">
                      <div className="card">
                        <div className="card-header">
                          <h6 className="card-title mb-0">Stock Status Distribution</h6>
                        </div>
                        <div className="card-body">
                          <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                              <Pie
                                data={getStockStatusData()}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                                outerRadius={80}
                                fill="#8884d8"
                                dataKey="value"
                              >
                                {getStockStatusData().map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={entry.color} />
                                ))}
                              </Pie>
                              <Tooltip />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </div>

                    {/* Category-wise Stock Bar Chart */}
                    <div className="col-md-6">
                      <div className="card">
                        <div className="card-header">
                          <h6 className="card-title mb-0">Stock by Category</h6>
                        </div>
                        <div className="card-body">
                          <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={getCategoryWiseData()}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis 
                                dataKey="category" 
                                angle={-45}
                                textAnchor="end"
                                height={80}
                                fontSize={12}
                              />
                              <YAxis />
                              <Tooltip 
                                formatter={(value, name) => [
                                  name === 'totalStock' ? `${value.toLocaleString()} units` : value,
                                  name === 'totalStock' ? 'Total Stock' : 'Product Count'
                                ]}
                              />
                              <Legend />
                              <Bar dataKey="totalStock" fill="#007bff" name="Total Stock" />
                              <Bar dataKey="productCount" fill="#28a745" name="Product Count" />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {stockData.length > 0 && (
                  <div className="row mt-4">
                    {/* Top Products by Stock */}
                    <div className="col-md-6">
                      <div className="card">
                        <div className="card-header">
                          <h6 className="card-title mb-0">Top 10 Products by Stock</h6>
                        </div>
                        <div className="card-body">
                          <ResponsiveContainer width="100%" height={400}>
                            <BarChart data={getTopProductsData()} layout="horizontal">
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis type="number" />
                              <YAxis 
                                type="category" 
                                dataKey="code" 
                                width={80}
                                fontSize={10}
                              />
                              <Tooltip 
                                formatter={(value, name) => [`${value.toLocaleString()}`, 'Stock Quantity']}
                                labelFormatter={(label) => {
                                  const product = getTopProductsData().find(p => p.code === label);
                                  return product ? `${product.name} (${product.code})` : label;
                                }}
                              />
                              <Bar dataKey="stock" fill="#17a2b8" />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </div>

                    {/* Stock Level Trends */}
                    <div className="col-md-6">
                      <div className="card">
                        <div className="card-header">
                          <h6 className="card-title mb-0">Stock Level Analysis by Category</h6>
                        </div>
                        <div className="card-body">
                          <ResponsiveContainer width="100%" height={400}>
                            <ComposedChart data={getStockTrendData()}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis 
                                dataKey="category" 
                                angle={-45}
                                textAnchor="end"
                                height={80}
                                fontSize={10}
                              />
                              <YAxis />
                              <Tooltip />
                              <Legend />
                              <Area 
                                type="monotone" 
                                dataKey="target" 
                                fill="#e9ecef" 
                                stroke="#6c757d"
                                name="Target Level"
                              />
                              <Bar dataKey="current" fill="#007bff" name="Current Stock" />
                              <Line 
                                type="monotone" 
                                dataKey="projected" 
                                stroke="#dc3545" 
                                strokeWidth={2}
                                name="Projected"
                              />
                            </ComposedChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Search Results Info */}
                {searchTerm && (
                  <div className="alert alert-info">
                    <i className="fa fa-info-circle"></i> 
                    Showing {filteredStockData.length} of {stockData.length} products matching &quot;{searchTerm}&quot;
                    {filteredStockData.length === 0 && (
                      <span className="ml-2">
                        <button 
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => setSearchTerm('')}
                        >
                          Clear search
                        </button>
                      </span>
                    )}
                  </div>
                )}
                
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Code</th>
                        <th>Product Name</th>
                        <th>Current Stock</th>
                        <th>Unit</th>
                        <th>Category</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredStockData.length > 0 ? (
                        <>
                          {filteredStockData.map((product, index) => {
                            const currentStock = parseFloat(product.stock_qty || 0);
                            const isLowStock = currentStock <= 10; // Simple threshold for demo
                            const isCritical = currentStock <= 5; // Critical threshold
                            
                            return (
                              <tr 
                                key={index}
                                onClick={() => handleProductClick(product)}
                                style={{ cursor: 'pointer' }}
                                className="hover-row"
                                title="Click to view product ledger"
                              >
                                <td>
                                  <strong>{product.code || 'N/A'}</strong>
                                </td>
                                <td>
                                  <strong>{product.name || 'N/A'}</strong>
                                </td>
                                <td>
                                  <span className={`${isCritical ? 'text-danger' : isLowStock ? 'text-warning' : 'text-success'}`}>
                                    {currentStock.toLocaleString()}
                                  </span>
                                </td>
                                <td>{product.unit_name || 'pcs'}</td>
                                <td>
                                  <span className="badge badge-info">
                                    {product.category_name || 'N/A'}
                                  </span>
                                </td>
                                <td>
                                  {isCritical ? (
                                    <span className="badge badge-danger">Critical</span>
                                  ) : isLowStock ? (
                                    <span className="badge badge-warning">Low Stock</span>
                                  ) : (
                                    <span className="badge badge-success">In Stock</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </>
                      ) : (
                        <tr>
                          <td colSpan="6" className="text-center text-muted">
                            {loading ? (
                              <div className="d-flex justify-content-center align-items-center py-3">
                                <div className="spinner-border spinner-border-sm text-primary mr-2" role="status">
                                  <span className="sr-only">Loading...</span>
                                </div>
                                Loading product stock data...
                              </div>
                            ) : searchTerm ? (
                              `No products found matching "${searchTerm}"`
                            ) : (
                              'No product stock data available'
                            )}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="am-footer">
          <span>
            Copyright ©. All Rights Reserved.{" "}
            <a href="http://limerickbd.com/">Limerick Resources Limited</a>
          </span>
        </div>
      </div>
    </div>
  );
};

export default SupplyChain;
